<template>
  <div class="simple-validate" data-testid="simple-validate">
    <div class="d-flex align-center justify-space-between mb-2">
      <h2 class="text-h6 mb-0">{{ $t('simple_mode.validate_title') }}</h2>
      <span
        v-if="remaining > 0"
        class="text-body-2 text-medium-emphasis"
        data-testid="simple-remaining-count"
      >
        {{ $t('simple_mode.remaining', { count: remaining }) }}
      </span>
    </div>

    <div v-if="loading" class="text-body-2">{{ $t('simple_mode.loading') }}</div>
    <div v-else-if="!current" class="text-body-2 text-medium-emphasis" data-testid="simple-validate-empty">
      {{ $t('simple_mode.validate_empty') }}
    </div>
    <div v-else class="simple-validate__strip">
      <div class="simple-validate__current" data-testid="simple-validate-current" @click="openModal">
        <annotated-image
          v-if="current.asset_url"
          :image_url="current.asset_url"
          :annotations="current.annotations"
          :zoom_enabled="false"
          :hide_annotation_ui="true"
          :max_height="280"
        />
        <div class="mt-2">
          <v-btn color="primary" data-testid="simple-validate-open" @click.stop="openModal">
            {{ $t('simple_mode.validate_open') }}
          </v-btn>
        </div>
      </div>
      <div
        v-if="peek"
        class="simple-validate__peek"
        data-testid="simple-validate-peek"
      >
        <img
          class="simple-validate__peek-img"
          :src="peekImageSrc"
          :alt="$t('simple_mode.next_image')"
        />
        <div class="simple-validate__peek-badge">
          {{ $t('simple_mode.more_to_annotate', { count: Math.max(0, remaining - 1) }) }}
        </div>
      </div>
    </div>

    <annotate-modal
      v-if="modalAssetId"
      :asset_id="modalAssetId"
      @close_modal="closeModal"
      @remove_item="onValidated"
    />
  </div>
</template>

<script>
import AnnotatedImage from '@/components/AnnotatedImage.vue';
import AnnotateModal from '@/components/AnnotateModal.vue';
import Annotation from '@/classes/annotation';
import {
  buildAssetStateFromNextResult,
  protectedAssetUrlWithToken,
} from '@/utils/annotatePreload';

const FILTERS = JSON.stringify([
  { type: 9, value: 'No' },
  { type: 61, value: 'No' },
  { type: 23, value: 'Yes' },
]);

export default {
  name: 'SimpleValidateCarousel',
  components: { AnnotatedImage, AnnotateModal },
  data() {
    return {
      loading: false,
      current: null,
      peek: null,
      remaining: 0,
      modalAssetId: null,
    };
  },
  computed: {
    peekImageSrc() {
      if (!this.peek?.asset_url) {
        return '';
      }
      return protectedAssetUrlWithToken(this.peek.asset_url, localStorage.token);
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      try {
        const count = await this.$api.asset.count_filter(JSON.parse(FILTERS));
        this.remaining = typeof count === 'number' ? count : 0;

        let result = await this.$api.asset.next_asset(FILTERS);
        if (!result || result.done || result.error || !result.asset) {
          this.current = null;
          this.peek = null;
          return;
        }
        this.current = buildAssetStateFromNextResult(result, this.$api_host, Annotation);

        const nextResult = await this.$api.asset.next_asset(FILTERS, null, [this.current.asset_id]);
        if (!nextResult || nextResult.done || nextResult.error || !nextResult.asset) {
          this.peek = null;
        } else {
          this.peek = buildAssetStateFromNextResult(nextResult, this.$api_host, Annotation);
        }
      } finally {
        this.loading = false;
      }
    },
    openModal() {
      if (this.current?.asset_id) {
        this.modalAssetId = this.current.asset_id;
      }
    },
    closeModal() {
      this.modalAssetId = null;
      this.refresh();
    },
    onValidated() {
      this.modalAssetId = null;
      this.refresh();
      this.$emit('validated');
    },
  },
};
</script>

<style scoped>
.simple-validate__strip {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
  gap: 0;
}
.simple-validate__current {
  flex: 1 1 auto;
  min-width: 0;
  max-width: calc(100% - 88px);
  cursor: pointer;
}
.simple-validate__current :deep(.annotated-image__canvas-wrap) {
  max-height: 280px;
  overflow: hidden;
}
.simple-validate__peek {
  flex: 0 0 80px;
  width: 80px;
  max-height: 280px;
  position: relative;
  overflow: hidden;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  background: #f0f0f0;
}
.simple-validate__peek-img {
  height: 100%;
  max-height: 280px;
  width: 100%;
  object-fit: cover;
  display: block;
}
.simple-validate__peek-badge {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 6px 4px;
  font-size: 11px;
  line-height: 1.2;
  text-align: center;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
}
</style>
